/**
 * POS Fast-Write Helper
 *
 * POS-only single-batch save path. Replaces the multi-step
 * createTransaction → generateSubTransactions → generateStockFlow pipeline
 * with one writeBatch commit, plus a single counter-allocation transaction.
 *
 * Compatibility: produces the EXACT same documents as the original path
 * (same fields, same shapes) so reports/history/double-entry are unaffected.
 *
 * Speed wins:
 *   - System accounts preloaded ONCE per POS session (preloadPOSCaches)
 *   - Default location preloaded ONCE per POS session
 *   - Line enrichment done client-side using cached items (no per-save reads)
 *   - All writes (transaction header + subTransactions + stockFlow) in 1 batch
 *
 * Result: ~6 round-trips → ~2 round-trips per sale.
 */

import {
  collection, doc, getDoc, getDocs, query, where, writeBatch, runTransaction
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { SYSTEM_ACCOUNTS, getTranType } from '../config/tranTypes';
import { enrichLineWithUnit, twoUnitsModuleOn, twoUnitLineFields, roundBaseQty } from './unitConversion';
import { localDateStr } from './localDate';
import { usageDocId } from './bankDiscountHelper';
import { posPoolIncrements, markZeroCostPosRows, commitPoolUpdates } from './itemCostEngine';

// Show Two Units (module item_two_units): the same qty in the item's second
// unit, saved on the line for receipts, views and the item ledger. Empty when
// the module is off or the item has no pair, so those saves are unchanged.
const twoUnitsFor = (line, catalog) => {
  if (!twoUnitsModuleOn() || !line || !line.itemId) return {};
  const item = (catalog || []).find(i => i.id === line.itemId);
  return twoUnitLineFields(item, line) || {};
};

const TRAN_CODE_TO_DOC_KEY = {
  PS: 'posCounterSale',
  SR: 'saleReturn'
};

// ─── Preload caches (call once when POS screen mounts) ────────────────────

/**
 * Load every cache the fast-write path needs so per-sale work is pure JS.
 * Returns { systemAccounts, defaultLocation, partyAccountByCustomerId }.
 */
export const preloadPOSCaches = async (companyId) => {
  const [systemAccounts, defaultLocation, partyAccountByCustomerId] = await Promise.all([
    preloadSystemAccounts(companyId),
    preloadDefaultLocation(companyId),
    preloadCustomerPartyAccounts(companyId)
  ]);
  return { systemAccounts, defaultLocation, partyAccountByCustomerId };
};

const preloadSystemAccounts = async (companyId) => {
  const allCodes = Object.values(SYSTEM_ACCOUNTS);
  const accountsRef = collection(db, 'companies', companyId, 'accounts');
  const cache = {};
  // Firestore `in` clause caps at 30, so chunk if needed.
  const chunks = [];
  for (let i = 0; i < allCodes.length; i += 30) chunks.push(allCodes.slice(i, i + 30));
  await Promise.all(chunks.map(async (codes) => {
    const q = query(accountsRef, where('code', 'in', codes));
    const snap = await getDocs(q);
    snap.docs.forEach(d => { const data = d.data(); cache[data.code] = { id: d.id, ...data }; });
  }));
  return cache;
};

const preloadDefaultLocation = async (companyId) => {
  const locRef = collection(db, 'companies', companyId, 'locations');
  const snap = await getDocs(locRef);
  if (snap.empty) return { id: null, locationName: 'Main' };
  const def = snap.docs.find(d => d.data().isDefault) || snap.docs[0];
  return { id: def.id, locationName: def.data().locationName || 'Main' };
};

const preloadCustomerPartyAccounts = async (companyId) => {
  const accountsRef = collection(db, 'companies', companyId, 'accounts');
  const q = query(accountsRef, where('linkedEntityType', '==', 'customer'));
  const snap = await getDocs(q);
  const map = {};
  snap.docs.forEach(d => {
    const data = d.data();
    if (data.linkedEntityId) map[data.linkedEntityId] = { id: d.id, ...data };
  });
  return map;
};

// ─── Doc number allocation (kept atomic via runTransaction) ───────────────

/**
 * Allocate the next document number for a POS-relevant tran type.
 * Exposed so callers can pre-allocate ahead of the batch commit and print
 * the receipt while the commit runs in the background.
 */
export const allocatePOSDocNumber = (companyId, tranTypeCode, docDate, branchId = null, counter = null) =>
  generateDocNumber(companyId, tranTypeCode, docDate, branchId, counter);

const generateDocNumber = async (companyId, tranTypeCode, docDate, branchId = null, counter = null) => {
  const tranType = getTranType(tranTypeCode);
  if (!tranType) throw new Error(`Invalid transaction type: ${tranTypeCode}`);

  let customPrefix = tranType.prefix;
  let padding = 5;
  let startNumber = 1;
  try {
    const docKey = TRAN_CODE_TO_DOC_KEY[tranTypeCode];
    if (docKey) {
      const settingsRef = doc(db, 'companies', companyId, 'settings', 'documentNumbering');
      const settingsSnap = await getDoc(settingsRef);
      if (settingsSnap.exists()) {
        const entry = settingsSnap.data()?.[docKey];
        if (entry) {
          if (entry.prefix && String(entry.prefix).trim()) {
            const raw = String(entry.prefix).trim();
            customPrefix = raw.endsWith('-') ? raw : `${raw}-`;
          }
          if (Number(entry.padding)) padding = Math.min(Math.max(Number(entry.padding), 3), 8);
          if (Number(entry.startNumber)) startNumber = Number(entry.startNumber);
        }
      }
    }
  } catch (e) { /* fall back to defaults */ }

  // Per-counter continuous numbering: each counter gets its own series, e.g.
  // PS-T1-00001. Counter doc + format MUST match the offline range allocator
  // (posDocCounterRange.allocateRange) so online/offline never clash. Falls back
  // to a single global series (PS-00001) when no counter is supplied.
  const counterKey = counter?.id ? `${tranTypeCode}__${counter.id}` : `${tranTypeCode}__cont`;
  const seg = counter?.counterCode ? `${counter.counterCode}-` : '';
  const counterRef = doc(db, 'companies', companyId, 'documentCounters', counterKey);

  return runTransaction(db, async (transaction) => {
    const counterDoc = await transaction.get(counterRef);
    let current = 0;
    if (counterDoc.exists()) {
      current = Number(counterDoc.data().current) || 0;
    }
    const currentNumber = Math.max(current, startNumber - 1) + 1;
    transaction.set(counterRef, { prefix: customPrefix, current: currentNumber, updatedAt: new Date().toISOString() });
    const padded = currentNumber.toString().padStart(padding, '0');
    return `${customPrefix}${seg}${padded}`;
  });
};

// ─── Strip undefined (Firestore rejects them) ─────────────────────────────

const stripUndefined = (obj) => {
  if (obj == null) return obj;
  if (Array.isArray(obj)) return obj.map(stripUndefined).filter(v => v !== undefined);
  if (typeof obj !== 'object') return obj;
  const out = {};
  Object.entries(obj).forEach(([k, v]) => {
    if (v === undefined) return;
    out[k] = typeof v === 'object' && v !== null ? stripUndefined(v) : v;
  });
  return out;
};

// ─── Accounting entry builders (POS-specific, mirror generatePOSSaleEntries) ─

const buildPOSSaleEntries = (base, data, sysAccts) => {
  const entries = [];
  const { totalAmount, taxAmount, subtotal, discountAmount, lines,
    posPayments, paymentMethod, partyAccountId, partyAccountCode, partyName } = data;
  const netAmount = (subtotal || 0) - (discountAmount || 0);

  // 1. Dr Cash/Bank/Customer (one or more, based on payments)
  if (posPayments && posPayments.length > 0) {
    for (const pmt of posPayments) {
      if (!(pmt.amount > 0)) continue;
      if (pmt.method === 'credit' && partyAccountId) {
        entries.push({ ...base,
          accountId: partyAccountId, accountCode: partyAccountCode,
          accountName: partyName ? `Customer: ${partyName}` : 'Customer Account',
          debitAmount: round2(pmt.amount), creditAmount: 0,
          description: `POS credit sale ${base.docNumber}` });
      } else if (pmt.accountId) {
        entries.push({ ...base,
          accountId: pmt.accountId, accountCode: pmt.accountCode || null,
          accountName: pmt.accountName || 'Cash/Bank',
          debitAmount: round2(pmt.amount), creditAmount: 0,
          description: `POS ${pmt.method} payment ${base.docNumber}` });
      }
    }
  } else if (paymentMethod === 'credit' && partyAccountId) {
    entries.push({ ...base,
      accountId: partyAccountId, accountCode: partyAccountCode,
      accountName: partyName ? `Customer: ${partyName}` : 'Customer Account',
      debitAmount: round2(totalAmount), creditAmount: 0,
      description: `POS credit sale ${base.docNumber}` });
  } else {
    const cash = sysAccts[SYSTEM_ACCOUNTS.CASH_IN_HAND];
    if (cash && totalAmount > 0) {
      entries.push({ ...base,
        accountId: cash.id, accountCode: cash.code, accountName: cash.name,
        debitAmount: round2(totalAmount), creditAmount: 0,
        description: `POS cash sale ${base.docNumber}` });
    }
  }

  // 2. Cr Sales Revenue
  if (netAmount > 0) {
    const sales = sysAccts[SYSTEM_ACCOUNTS.SALES_GENERAL];
    if (sales) entries.push({ ...base,
      accountId: sales.id, accountCode: sales.code, accountName: sales.name,
      debitAmount: 0, creditAmount: round2(netAmount),
      description: `POS sales ${base.docNumber}` });
  }

  // 3. Cr GST Output
  if (taxAmount > 0) {
    const gst = sysAccts[SYSTEM_ACCOUNTS.GST_OUTPUT];
    if (gst) entries.push({ ...base,
      accountId: gst.id, accountCode: gst.code, accountName: gst.name,
      debitAmount: 0, creditAmount: round2(taxAmount),
      description: `GST Output on POS ${base.docNumber}` });
  }

  // 4. COGS: Dr COGS / Cr Inventory Control
  const totalCOGS = (lines || []).reduce((sum, l) => {
    if (l.itemType === 'service') return sum;
    return sum + (Number(l.quantity) || 0) * (Number(l.unitCost) || 0);
  }, 0);
  if (totalCOGS > 0) {
    const cogs = sysAccts[SYSTEM_ACCOUNTS.COGS];
    const inv = sysAccts[SYSTEM_ACCOUNTS.INVENTORY_CONTROL];
    if (cogs) entries.push({ ...base,
      accountId: cogs.id, accountCode: cogs.code, accountName: cogs.name,
      debitAmount: round2(totalCOGS), creditAmount: 0,
      description: `COGS for POS ${base.docNumber}` });
    if (inv) entries.push({ ...base,
      accountId: inv.id, accountCode: inv.code, accountName: inv.name,
      debitAmount: 0, creditAmount: round2(totalCOGS),
      description: `Inventory issued POS ${base.docNumber}` });
  }
  return entries;
};

const buildPOSReturnEntries = (base, data, sysAccts) => {
  const entries = [];
  const { totalAmount, taxAmount, subtotal, discountAmount, lines,
    partyAccountId, partyAccountCode, partyName,
    posPayments, paymentMethod } = data;
  const netAmount = (subtotal || 0) - (discountAmount || 0);
  const customerName = partyName ? `Customer: ${partyName}` : 'Customer Account';

  // 1. Dr Sales Returns
  if (netAmount > 0) {
    const sr = sysAccts[SYSTEM_ACCOUNTS.SALES_RETURNS];
    if (sr) entries.push({ ...base,
      accountId: sr.id, accountCode: sr.code, accountName: sr.name,
      debitAmount: round2(netAmount), creditAmount: 0,
      description: `Sales return ${base.docNumber}` });
  }

  // 2. Dr GST Output reversal
  if (taxAmount > 0) {
    const gst = sysAccts[SYSTEM_ACCOUNTS.GST_OUTPUT];
    if (gst) entries.push({ ...base,
      accountId: gst.id, accountCode: gst.code, accountName: gst.name,
      debitAmount: round2(taxAmount), creditAmount: 0,
      description: `GST Output reversal ${base.docNumber}` });
  }

  // 3. Cr side — refund cash/bank if cash refund, else Cr customer (credit note).
  // POS return drives the credit side from posPayments[] (refund method) the same
  // way a sale drives the debit side. Fallback to customer credit if no payment.
  let creditedTotal = 0;
  if (posPayments && posPayments.length > 0) {
    for (const pmt of posPayments) {
      if (!(pmt.amount > 0)) continue;
      if (pmt.method === 'credit' && partyAccountId) {
        entries.push({ ...base,
          accountId: partyAccountId, accountCode: partyAccountCode, accountName: customerName,
          debitAmount: 0, creditAmount: round2(pmt.amount),
          description: `Credit note ${base.docNumber}` });
      } else if (pmt.accountId) {
        entries.push({ ...base,
          accountId: pmt.accountId, accountCode: pmt.accountCode || null,
          accountName: pmt.accountName || 'Cash/Bank',
          debitAmount: 0, creditAmount: round2(pmt.amount),
          description: `POS ${pmt.method} refund ${base.docNumber}` });
      }
      creditedTotal += Number(pmt.amount) || 0;
    }
  }
  // Any unallocated remainder → customer credit
  const remainder = round2((totalAmount || 0) - creditedTotal);
  if (remainder > 0 && partyAccountId) {
    entries.push({ ...base,
      accountId: partyAccountId, accountCode: partyAccountCode, accountName: customerName,
      debitAmount: 0, creditAmount: remainder,
      description: `Credit note ${base.docNumber}` });
  }

  // 4. COGS reversal: Dr Inventory / Cr COGS
  const totalCOGS = (lines || []).reduce((sum, l) => {
    if (l.itemType === 'service') return sum;
    return sum + (Number(l.quantity) || 0) * (Number(l.unitCost) || 0);
  }, 0);
  if (totalCOGS > 0) {
    const inv = sysAccts[SYSTEM_ACCOUNTS.INVENTORY_CONTROL];
    const cogs = sysAccts[SYSTEM_ACCOUNTS.COGS];
    if (inv) entries.push({ ...base,
      accountId: inv.id, accountCode: inv.code, accountName: inv.name,
      debitAmount: round2(totalCOGS), creditAmount: 0,
      description: `Inventory returned ${base.docNumber}` });
    if (cogs) entries.push({ ...base,
      accountId: cogs.id, accountCode: cogs.code, accountName: cogs.name,
      debitAmount: 0, creditAmount: round2(totalCOGS),
      description: `COGS reversal for ${base.docNumber}` });
  }
  return entries;
};

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// ─── Bank card discount (module pos_bank_discounts) ───────────────────────
// The bank's share of the discount is a claim on the bank:
//   sale:   Dr Bank Discount Claim – <bank>   Cr Bank Discount Recovery
//   return: Dr Bank Discount Recovery         Cr Bank Discount Claim – <bank>
// Only added when the sale carries bank-discount data, so every other POS
// sale produces exactly the same entries as before.
const buildBankShareEntries = (base, bd, isReturn) => {
  const amt = round2(bd?.bankShareAmount);
  if (!(amt > 0) || !bd.claimAccountId || !bd.recoveryAccountId) return [];
  const claim = { accountId: bd.claimAccountId, accountCode: bd.claimAccountCode || null, accountName: bd.claimAccountName || 'Bank Discount Claim' };
  const recov = { accountId: bd.recoveryAccountId, accountCode: bd.recoveryAccountCode || null, accountName: bd.recoveryAccountName || 'Bank Discount Recovery' };
  const what = `${bd.label || bd.bankName || 'Bank'} card discount (bank share ${bd.bankSharePct ?? ''}%)`;
  return isReturn
    ? [
        { ...base, ...recov, debitAmount: amt, creditAmount: 0, description: `Reversal: ${what} ${base.docNumber}` },
        { ...base, ...claim, debitAmount: 0, creditAmount: amt, description: `Reversal: ${what} ${base.docNumber}` }
      ]
    : [
        { ...base, ...claim, debitAmount: amt, creditAmount: 0, description: `${what} ${base.docNumber}` },
        { ...base, ...recov, debitAmount: 0, creditAmount: amt, description: `${what} ${base.docNumber}` }
      ];
};

// Header copy of the bank discount (no usage counters).
const bankDiscountHeader = (bd) => {
  if (!bd) return null;
  const { usage, ...rest } = bd;
  return rest;
};

// ─── StockFlow row builder (POS-specific, mirrors generateStockFlow for PS/SR) ─

const buildStockFlowRows = (base, data, items, defaultLocation) => {
  // PS = out, SR = in
  const direction = data.tranTypeCode === 'PS' ? 'out' : 'in';
  const locationId = data.locationId || defaultLocation?.id || null;
  const locationName = data.locationName || defaultLocation?.locationName || 'Main';

  const validLines = (data.lines || []).filter(l => l.itemId && l.itemType !== 'service');
  return validLines.map((line, idx) => {
    const item = items.find(i => i.id === line.itemId) || {};
    // Client-side enrichment (replaces the per-save Firestore reads).
    const enriched = enrichLineWithUnit(item, line);
    const enteredQuantity = Number(line.quantity) || 0;
    const enteredUnitPrice = Number(line.unitPrice) || 0;
    const enteredUnitCost = Number(line.unitCost) || 0;
    const enteredUnitCode = line.unitCode || line.unitOfMeasure || '';
    const conversion = Number(enriched.conversion) || 1;
    // Show Two Units: exact base qty (14.400000001 → 14.4) + second unit for the ledger.
    const two = line.secondUnitCode ? { secondUnitCode: line.secondUnitCode, secondQuantity: Number(line.secondQuantity) || 0 } : null;
    const quantity = two ? roundBaseQty(Number(enriched.baseQuantity) || enteredQuantity) : (Number(enriched.baseQuantity) || enteredQuantity);
    const baseUnitCost = Number(enriched.baseUnitCost) || enteredUnitCost;
    const baseUnitPrice = Number(enriched.baseUnitPrice) || enteredUnitPrice;
    const baseUnit = enriched.baseUnitCode || item.unitOfMeasure || enteredUnitCode || '';

    return {
      transactionId: base.transactionId,
      tranTypeCode: data.tranTypeCode,
      docNumber: base.docNumber,
      docDate: data.docDate,
      financialYearId: data.financialYearId,
      branchId: data.branchId || null,
      branchName: data.branchName || null,
      locationId,
      locationName,
      status: data.status || 'Posted',
      direction,
      itemId: line.itemId,
      itemCode: line.itemCode || '',
      itemName: line.itemName || '',
      description: line.description || '',
      quantity,
      unitCost: baseUnitCost,
      unitPrice: baseUnitPrice,
      lineTotal: round2(quantity * baseUnitCost),
      // INV-03 fix (2026-09-30): stock value reports and the running average
      // read totalCost — POS rows never had it, so POS sales never reduced
      // stock value and POS returns diluted the average cost.
      totalCost: round2(quantity * baseUnitCost),
      unitOfMeasure: baseUnit,
      // Multi-unit audit fields (matches stockFlowHelper.generateStockFlow shape)
      enteredQuantity,
      enteredUnitPrice,
      enteredUnitCost,
      enteredUnitCode,
      conversion,
      ...(two || {}),
      lineNumber: idx + 1,
      createdAt: base.createdAt,
      createdBy: base.createdBy
    };
  });
};

// ─── Plain-words reason for an unbalanced entry set ───────────────────────
// Only words the error — the balance check itself is unchanged.
const SYS_ACCOUNT_LABELS = {
  [SYSTEM_ACCOUNTS.CASH_IN_HAND]: 'Cash in Hand',
  [SYSTEM_ACCOUNTS.SALES_GENERAL]: 'Sales',
  [SYSTEM_ACCOUNTS.SALES_RETURNS]: 'Sales Returns',
  [SYSTEM_ACCOUNTS.GST_OUTPUT]: 'GST Output',
  [SYSTEM_ACCOUNTS.COGS]: 'Cost of Goods Sold',
  [SYSTEM_ACCOUNTS.INVENTORY_CONTROL]: 'Inventory'
};

const unbalancedReasons = (tranTypeCode, data, sysAccts) => {
  const out = [];
  const need = (code) => {
    if (!sysAccts[code]) out.push(`system account "${SYS_ACCOUNT_LABELS[code] || code}" (${code}) is missing`);
  };
  const netAmount = (data.subtotal || 0) - (data.discountAmount || 0);
  if (netAmount > 0) need(tranTypeCode === 'PS' ? SYSTEM_ACCOUNTS.SALES_GENERAL : SYSTEM_ACCOUNTS.SALES_RETURNS);
  if (data.taxAmount > 0) need(SYSTEM_ACCOUNTS.GST_OUTPUT);
  const totalCOGS = (data.lines || []).reduce((sum, l) => (l.itemType === 'service' ? sum
    : sum + (Number(l.quantity) || 0) * (Number(l.unitCost) || 0)), 0);
  if (totalCOGS > 0 && !sysAccts[SYSTEM_ACCOUNTS.COGS] !== !sysAccts[SYSTEM_ACCOUNTS.INVENTORY_CONTROL]) {
    need(SYSTEM_ACCOUNTS.COGS);
    need(SYSTEM_ACCOUNTS.INVENTORY_CONTROL);
  }
  const pays = data.posPayments || [];
  pays.forEach((pmt) => {
    if (!(pmt.amount > 0)) return;
    if (pmt.method === 'credit' && !data.partyAccountId) out.push('the customer has no ledger account for the credit amount');
    else if (pmt.method !== 'credit' && !pmt.accountId) out.push(`the ${pmt.method || 'payment'} amount has no cash / bank account`);
  });
  if (pays.length === 0 && tranTypeCode === 'PS' && !(data.paymentMethod === 'credit' && data.partyAccountId)) {
    need(SYSTEM_ACCOUNTS.CASH_IN_HAND);
  }
  const paid = round2(pays.reduce((s, pmt) => s + (pmt.amount > 0 ? Number(pmt.amount) : 0), 0));
  if (pays.length > 0 && tranTypeCode === 'PS' && Math.abs(paid - round2(data.totalAmount)) > 0.01) {
    out.push(`payments ${paid.toFixed(2)} do not equal the bill total ${round2(data.totalAmount).toFixed(2)}`);
  }
  if (tranTypeCode === 'SR' && round2(data.totalAmount) - paid > 0 && !data.partyAccountId) {
    out.push('the customer has no ledger account for the refund');
  }
  return out;
};

// ─── Build (no writes, no reads) ──────────────────────────────────────────

/**
 * Build the complete POS save — transaction header, ledger rows and stock rows,
 * each with its document id — WITHOUT writing anything and WITHOUT any Firestore
 * read (ids are generated on the device). Runs every check that would otherwise
 * make the save fail (caches loaded, entries balance), so the POS can call it
 * BEFORE the receipt is printed. The documents are exactly the ones
 * savePOSTransactionFast has always written.
 *
 * @param {object} opts { docNumber, docDate, validateOnly } — validateOnly: run
 *                      the checks only (no doc number needed, no stock rows built).
 * @returns {object} prepared bundle — pass it back as saleData.prepared.
 */
export const buildPOSSaleBundle = (companyId, saleData, userId, caches, opts = {}) => {
  if (!caches?.systemAccounts) throw new Error('POS caches not loaded — call preloadPOSCaches first.');

  const {
    cart, totals, payments, paymentMethod,
    customerId, customerName,
    financialYearId, cashierName,
    branchId, branchName,
    counterId, counterCode, shiftId,
    returnMode, returnOriginalId
  } = saleData;

  // Branch → Location: prefer the location assigned to this branch (passed from
  // POS). Falls back to the company default location only if none is assigned.
  const effLocationId = saleData.locationId || caches.defaultLocation?.id || null;
  const effLocationName = saleData.locationName || caches.defaultLocation?.locationName || null;

  const tranTypeCode = returnMode ? 'SR' : 'PS';
  const docDate = opts.docDate || localDateStr(); // POS-06: Pakistan (device) date, not UTC
  const partyAccount = caches.partyAccountByCustomerId[customerId] || null;
  const partyAccountId = partyAccount?.id || null;
  const partyAccountCode = partyAccount?.code || null;

  // Lines for the transaction header.
  const lines = cart.map((item, idx) => ({
    lineNumber: idx + 1,
    itemId: item.itemId,
    itemCode: item.itemCode || '',
    itemName: item.itemName || '',
    description: item.description || '',
    quantity: item.quantity,
    unitPrice: item.priceExclTax || item.unitPrice,
    unitCost: item.unitCost || 0,
    discountPercent: item.discountPercent || 0,
    discountAmount: item.discountAmount || 0,
    taxable: item.taxable !== false,
    taxRate: item.taxRate || 0,
    taxAmount: item.taxAmount || 0,
    lineTotal: item.lineTotal || 0,
    totalCost: item.totalCost || 0,
    unitOfMeasure: item.unitOfMeasure || 'Pcs',
    unitCode: item.unitCode || item.unitOfMeasure || '',
    ...(Number(item.bankDiscountAmount) > 0 ? { bankDiscountAmount: round2(item.bankDiscountAmount) } : {}),
    ...twoUnitsFor(item, saleData.itemsCatalog)
  }));

  const docNumber = saleData.preAllocatedDocNumber || opts.docNumber || (opts.validateOnly ? 'NEW' : null);
  if (!docNumber) throw new Error('POS document number missing.');

  // Header
  const timestamp = new Date().toISOString();
  const transactionId = doc(collection(db, 'companies', companyId, 'transactions')).id;

  const transactionDoc = stripUndefined({
    tranTypeCode,
    docNumber,
    docDate,
    financialYearId,
    branchId: branchId || null,
    branchName: branchName || null,
    counterId: counterId || null,
    counterCode: counterCode || null,
    shiftId: shiftId || null,
    posDoc: true, // POS-03: saved from the POS counter (sale or return)
    partyType: 'customer',
    partyId: customerId,
    partyName: customerName,
    partyAccountId,
    partyAccountCode,
    subtotal: totals.subtotal,
    discountPercent: 0,
    discountAmount: totals.totalDiscount,
    taxableAmount: totals.subtotal - totals.totalDiscount,
    taxAmount: totals.taxAmount,
    whtRate: 0,
    whtAmount: 0,
    whtAccountId: null,
    totalAmount: totals.totalAmount,
    grossAmount: totals.subtotal,
    totalDiscount: totals.totalDiscount,
    totalGST: totals.taxAmount,
    totalWHT: 0,
    netAmount: totals.totalAmount,
    paidAmount: returnMode ? 0 : totals.totalAmount,
    balanceAmount: 0,
    status: 'Posted',
    parentId: returnOriginalId || null,
    childIds: [],
    locationId: effLocationId,
    locationName: effLocationName,
    notes: returnMode ? `POS Return against receipt` : 'POS Counter Sale',
    internalNotes: '',
    lines,
    // POS-specific
    posPayments: payments,
    paymentMethod,
    cashierName: cashierName || '',
    posTransaction: true,
    ...(saleData.bankDiscount && !returnMode ? { bankDiscount: bankDiscountHeader(saleData.bankDiscount) } : {}),
    ...(saleData.bankDiscountReturn && returnMode ? { bankDiscountReturn: saleData.bankDiscountReturn } : {}),
    postedAt: timestamp,
    postedBy: userId,
    createdAt: timestamp,
    createdBy: userId,
    updatedAt: timestamp,
    updatedBy: userId
  });

  // SubTransactions (Dr/Cr ledger)
  const baseSub = {
    transactionId,
    tranTypeCode,
    docNumber,
    docDate,
    financialYearId,
    branchId: branchId || null,
    branchName: branchName || null,
    counterId: counterId || null,
    counterCode: counterCode || null,
    shiftId: shiftId || null,
    status: 'Posted',
    createdAt: timestamp,
    createdBy: userId
  };
  const entryData = { ...transactionDoc, partyAccountId, partyAccountCode };
  const subEntries = tranTypeCode === 'PS'
    ? buildPOSSaleEntries(baseSub, entryData, caches.systemAccounts)
    : buildPOSReturnEntries(baseSub, entryData, caches.systemAccounts);
  if (tranTypeCode === 'PS' && saleData.bankDiscount) {
    subEntries.push(...buildBankShareEntries(baseSub, saleData.bankDiscount, false));
  } else if (tranTypeCode === 'SR' && saleData.bankDiscountReturn) {
    subEntries.push(...buildBankShareEntries(baseSub, saleData.bankDiscountReturn, true));
  }

  // Balance check
  if (subEntries.length > 0) {
    const dr = subEntries.reduce((s, e) => s + (Number(e.debitAmount) || 0), 0);
    const cr = subEntries.reduce((s, e) => s + (Number(e.creditAmount) || 0), 0);
    if (Math.abs(dr - cr) > 0.01) {
      const why = unbalancedReasons(tranTypeCode, entryData, caches.systemAccounts);
      throw new Error(
        `POS accounting unbalanced for ${tranTypeCode} ${docNumber}: ` +
        `Dr ${dr.toFixed(2)} ≠ Cr ${cr.toFixed(2)}. ` +
        (why.length ? `Reason: ${why.join('; ')}. ` : '') +
        `Check Chart of Accounts → click "Repair COA" to seed missing system accounts.`
      );
    }
  }
  if (opts.validateOnly) return { transactionId, docNumber, docDate, tranTypeCode, validateOnly: true };

  // StockFlow rows
  const baseStock = { transactionId, docNumber, createdAt: timestamp, createdBy: userId };
  const stockRows = buildStockFlowRows(baseStock, {
    tranTypeCode,
    docDate,
    financialYearId,
    branchId,
    branchName,
    locationId: effLocationId,
    locationName: effLocationName,
    status: 'Posted',
    lines
  }, saleData.itemsCatalog || [], caches.defaultLocation);
  // INV-04: a POS line with no cost waits for the next receipt's cost.
  markZeroCostPosRows(stockRows);

  // Document ids, generated on the device — the same ids are used whether the
  // sale is saved now or later from the offline outbox.
  const subRef = collection(db, 'companies', companyId, 'subTransactions');
  const stockRef = collection(db, 'companies', companyId, 'stockFlow');
  const subIds = subEntries.map(() => doc(subRef).id);
  const stockIds = stockRows.map(() => doc(stockRef).id);

  // Bank card discount: the card's usage counters + the report log (sale only).
  let bankUsage = null;
  let bankLog = null;
  if (tranTypeCode === 'PS' && saleData.bankDiscount) {
    const bd = saleData.bankDiscount;
    if (bd.usage && bd.offerId) {
      bankUsage = {
        id: usageDocId(bd.offerId, bd.cardFirst6, bd.cardLast4),
        data: {
          offerId: bd.offerId,
          cardMask: bd.cardMask || '',
          dayKey: bd.usage.dayKey, dayCount: bd.usage.dayCount,
          monthKey: bd.usage.monthKey, monthCount: bd.usage.monthCount, monthAmount: bd.usage.monthAmount,
          updatedAt: timestamp
        }
      };
    }
    bankLog = stripUndefined({
      transactionId, docNumber, docDate, financialYearId,
      branchId: branchId || null, branchName: branchName || null,
      counterCode: counterCode || null, cashierName: cashierName || '',
      offerId: bd.offerId, bankName: bd.bankName || '', cardType: bd.cardType || '', label: bd.label || '',
      percent: bd.percent || 0, cardMask: bd.cardMask || '',
      billAmount: totals.totalAmount,
      discountAmount: round2(bd.amount),
      shopSharePct: bd.shopSharePct, bankSharePct: bd.bankSharePct,
      shopShareAmount: round2(bd.shopShareAmount), bankShareAmount: round2(bd.bankShareAmount),
      claimAccountId: bd.claimAccountId || null,
      reversedAmount: 0,
      status: 'Posted',
      createdAt: timestamp, createdBy: userId
    });
  }

  return {
    transactionId,
    docNumber,
    docDate,
    tranTypeCode,
    financialYearId,
    returnOriginalId: returnOriginalId || null,
    transactionDoc,
    subEntries,
    subIds,
    stockRows,
    stockIds,
    hasBankDiscount: !!saleData.bankDiscount,
    bankUsage,
    bankLog
  };
};

/**
 * The prepared sale in the offline-outbox shape (posLocalCache.savePOSSaleLocally
 * → posSyncWorker.pushBundle). Same transaction id as the online attempt, so the
 * sync worker skips it if that attempt did reach the cloud.
 */
export const preparedToOutboxBundle = (p) => ({
  transaction: { id: p.transactionId, ...p.transactionDoc },
  subTransactions: p.subEntries.map((e, i) => ({ ...e, id: p.subIds[i] })),
  stockFlow: p.stockRows.map((r, i) => ({ ...r, id: p.stockIds[i] })),
  // The bank-discount report log row belongs to the sale (keyed by its id).
  ...(p.bankLog ? { extraWrites: [{ collection: 'bankDiscountLog', id: p.transactionId, data: p.bankLog }] } : {})
});

// ─── Commit ───────────────────────────────────────────────────────────────

const commitPOSSaleBundle = async (companyId, p) => {
  const { transactionId, docNumber, docDate, transactionDoc, subEntries, stockRows, returnOriginalId } = p;
  const txnDocRef = doc(db, 'companies', companyId, 'transactions', transactionId);
  // INV-04: the items' running averages move in the same batch (increments, no reads).
  const poolUpdates = posPoolIncrements(companyId, stockRows, p.financialYearId);

  // Single batched write
  const batch = writeBatch(db);
  batch.set(txnDocRef, transactionDoc);

  const subRef = collection(db, 'companies', companyId, 'subTransactions');
  subEntries.forEach((entry, i) => {
    batch.set(doc(subRef, p.subIds[i]), stripUndefined(entry));
  });

  const stockRef = collection(db, 'companies', companyId, 'stockFlow');
  stockRows.forEach((row, i) => {
    batch.set(doc(stockRef, p.stockIds[i]), stripUndefined(row));
  });
  // INV-04: a very large bill could pass Firestore's 500-writes-per-save limit
  // once the item updates are added — then they follow in a second save.
  const mainOps = 1 + subEntries.length + stockRows.length + (p.hasBankDiscount ? 2 : 0);
  const poolInMain = mainOps + poolUpdates.length <= 480;
  if (poolInMain) poolUpdates.forEach(u => batch.update(u.ref, u.data));

  // Bank card discount: the card's usage counters + the report log, in the
  // SAME batch so they can never disagree with the sale.
  if (p.bankUsage) {
    batch.set(doc(db, 'companies', companyId, 'bankDiscountUsage', p.bankUsage.id), p.bankUsage.data, { merge: true });
  }
  if (p.bankLog) {
    batch.set(doc(db, 'companies', companyId, 'bankDiscountLog', transactionId), p.bankLog);
  }

  await batch.commit();
  if (!poolInMain) await commitPoolUpdates(poolUpdates);

  // Post-commit: link return to parent (separate, non-critical)
  if (returnOriginalId) {
    try {
      const parentRef = doc(db, 'companies', companyId, 'transactions', returnOriginalId);
      const parentSnap = await getDoc(parentRef);
      if (parentSnap.exists()) {
        const existingChildren = parentSnap.data().childIds || [];
        if (!existingChildren.includes(transactionId)) {
          const writeBatch2 = writeBatch(db);
          writeBatch2.update(parentRef, {
            childIds: [...existingChildren, transactionId],
            updatedAt: new Date().toISOString()
          });
          await writeBatch2.commit();
        }
      }
    } catch (e) { /* non-fatal */ }
  }

  return {
    id: transactionId,
    docNumber,
    docDate,
    ...transactionDoc,
    // Expose the bundle we just committed so callers (the online/offline
    // router) can mirror it to local Dexie — keeps the offline cache fresh
    // even on online sales. Hidden under an underscore key so existing call
    // sites that spread the result onto state don't accidentally store it.
    _bundle: {
      transaction: { id: transactionId, ...transactionDoc },
      subTransactions: subEntries,
      stockFlow: stockRows
    }
  };
};

// ─── Main save entry point ────────────────────────────────────────────────

/**
 * Single-batch POS save.
 *
 * @param {string} companyId
 * @param {object} saleData  Same shape as posHelper.savePOSTransaction's saleData,
 *                           with: cart (priced lines), totals, payments, paymentMethod,
 *                           customerId, customerName, financialYearId, cashierName,
 *                           branchId, branchName, returnMode, returnOriginalId.
 *                           Optional `prepared` (from buildPOSSaleBundle): commit
 *                           exactly that bundle (same ids) instead of building.
 * @param {string} userId
 * @param {object} caches    From preloadPOSCaches: { systemAccounts, defaultLocation, partyAccountByCustomerId }
 * @returns {object} { id, docNumber, docDate, ...transactionDoc }
 */
export const savePOSTransactionFast = async (companyId, saleData, userId, caches) => {
  if (!caches?.systemAccounts) throw new Error('POS caches not loaded — call preloadPOSCaches first.');

  let prepared = saleData.prepared || null;
  if (!prepared) {
    const tranTypeCode = saleData.returnMode ? 'SR' : 'PS';
    const docDate = localDateStr(); // POS-06: Pakistan (device) date, not UTC
    // Doc number (atomic counter). Reuse a pre-allocated one if caller passed it
    // — lets the UI print the receipt while we run the batch commit in parallel.
    const docNumber = saleData.preAllocatedDocNumber
      || await generateDocNumber(companyId, tranTypeCode, docDate, saleData.branchId || null,
           saleData.counterId ? { id: saleData.counterId, counterCode: saleData.counterCode } : null);
    prepared = buildPOSSaleBundle(companyId, saleData, userId, caches, { docNumber, docDate });
  }
  return commitPOSSaleBundle(companyId, prepared);
};

export default {
  preloadPOSCaches,
  allocatePOSDocNumber,
  buildPOSSaleBundle,
  preparedToOutboxBundle,
  savePOSTransactionFast
};
